import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { BrowserModule } from "@angular/platform-browser";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ServiceWorkerModule } from "@angular/service-worker";
import { ShellComponent } from "./shell/shell.component";
import { SimpleFilter } from "./pipes/simple.filter";
import localeEnGB from "@angular/common/locales/en-GB";
import localeEnUS from "@angular/common/locales/en";
import localeFr from "@angular/common/locales/fr";
import localeZh from "@angular/common/locales/zh";
import { Uptime } from "./pipes/uptime.pipe";
import { HashLocationStrategy, LocationStrategy, registerLocaleData } from "@angular/common";
import { isDevMode, LOCALE_ID, NgModule } from "@angular/core";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ErrorStandardiser } from "./core/interceptors/error-standardiser";
import { MaterialModule } from "./core/material-module";
import { ConfirmComponent } from "./modals/confirm/confirm.component";
import { DashboardComponent } from "./dashboard/dashboard.component";
import { ModalHandler } from "./core/modal-handler";
import { SearchSelectComponent } from "./core/search-select/search-select.component";
import { ChoiceComponent } from "./modals/choice/choice.component";
import { AllSeeingEyeComponent } from "./core/all-seeing-eye";
import { InputComponent } from "./modals/input/input.component";
import { FormatIDDisplay } from "./transforms/format-id-string";
import { provideMomentDateAdapter } from "@angular/material-moment-adapter";
import { MAT_DATE_LOCALE, MatDateFormats } from "@angular/material/core";
import { JwtInterceptor } from "./core/interceptors/jwt";
import { LoginComponent } from "./login/login.component";
import { AuthGuard } from "./core/interceptors/auth-guard";
import { PasswordInputComponent } from "./core/password-input/password-input.component";
import { AccountComponent } from "./account/account.component";
import { CookiesComponent } from "./cookies/cookies.component";
import { AccountDetailsTabComponent } from "./account/tabs/account-details-tab/account-details-tab.component";
import { LicenseActivationComponent } from "./manageLicenses/license-activation/license-activation.component";
import { ActivationDetailsComponent } from "./manageLicenses/activation-details/activation-details.component";
import { ActivationFilterPipe } from "./pipes/activation-filter.pipe";
import { ActivityFilter } from "./pipes/activity-filter.pipe";
import { ActivityTabComponent } from "./manageLicenses/activity/activity-tab.component";
import { AddContractsTabComponent } from "./account/tabs/add-contracts-tab/add-contracts-tab.component";
import { ChangePasswordTabComponent } from "./account/tabs/change-password--tab/change-password-tab.component";
import { ContractSummaryComponent } from "./manageLicenses/contract-summary/contract-summary.component";
import { DeleteComponent } from "./account/tabs/delete/delete.component";
import { DocsComponent } from "./downloads/docs/docs.component";
import { DownloadsComponent } from "./downloads/downloads.component";
import { FilesizePipe } from "./pipes/filesize.pipe";
import { InstallersComponent } from "./downloads/installers/installers.component";
import { IsosComponent } from "./downloads/isos/isos.component";
import { LicenseDetailsComponent } from "./manageLicenses/license-details/license-details.component";
import { ManageLicensesComponent } from "./manageLicenses/manageLicenses.component";
import { DownloadTabsComponent } from "./downloads/download-tabs/download-tabs.component";
import { OfflineSurrenderComponent } from "./manageLicenses/offline-surrender/offline-surrender.component";
import { RegisterComponent } from "./register/register.component";
import { TokenRequestComponent } from "./manageLicenses/token-request/token-request.component";
import { ResetComponent } from "./reset/reset.component";
import { ResetQuestionTabComponent } from "./account/tabs/reset-question-tab/reset-question-tab.component";
import { LicenseSurrenderComponent } from "./manageLicenses/license-surrender/license-surrender.component";
import { TokenDetailsComponent } from "./manageLicenses/token-details/token-details.component";
import { LicenseOverviewComponent } from "./manageLicenses/license-overview/license-overview.component";
import { InviteUsersTabComponent } from "./account/tabs/invite-users-tab/invite-users-tab.component";

// Setup American English, British English, French and Chinese locales
registerLocaleData(localeEnUS, "en-US");
registerLocaleData(localeEnGB, "en-GB");
registerLocaleData(localeFr);
registerLocaleData(localeZh);
export let language: string;
if (window.navigator.languages) {
  language = window.navigator.languages[0];
} else {
  // @ts-expect-error userLanguage exists on older IE-era browsers only
  language = window.navigator.userLanguage || window.navigator.language;
}

export function getToken(): string {
  return localStorage.getItem("token");
}

export const LOCALE_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: "L LT", // L = localized date, LT = localized time
  },
  display: {
    dateInput: "L LT",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

if (!language.startsWith("en") && !language.startsWith("fr") && !language.startsWith("zh")) language = "en-US"; // Default to US English
@NgModule({
  declarations: [
    AllSeeingEyeComponent,
    AppComponent,
    ChoiceComponent,
    InputComponent,
    LoginComponent,
    ConfirmComponent,
    DashboardComponent,
    AccountComponent,
    CookiesComponent,
    AccountDetailsTabComponent,
    LicenseActivationComponent,
    ActivationDetailsComponent,
    ActivationFilterPipe,
    ActivityFilter,
    ActivityTabComponent,
    AddContractsTabComponent,
    ChangePasswordTabComponent,
    FormatIDDisplay,
    ContractSummaryComponent,
    DeleteComponent,
    DocsComponent,
    DownloadsComponent,
    FilesizePipe,
    InputComponent,
    InstallersComponent,
    IsosComponent,
    LicenseDetailsComponent,
    ManageLicensesComponent,
    DownloadTabsComponent,
    OfflineSurrenderComponent,
    SearchSelectComponent,
    RegisterComponent,
    TokenRequestComponent,
    ResetComponent,
    ResetQuestionTabComponent,
    ShellComponent,
    SimpleFilter,
    LicenseSurrenderComponent,
    TokenDetailsComponent,
    Uptime,
    LicenseOverviewComponent,
    PasswordInputComponent,
    InviteUsersTabComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    FormsModule,
    MaterialModule,
    ReactiveFormsModule,
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: !isDevMode(),
      registrationStrategy: "registerWhenStable:30000",
    }),
  ],
  providers: [
    AuthGuard,
    { provide: HTTP_INTERCEPTORS, useClass: ErrorStandardiser, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: language },
    { provide: MAT_DATE_LOCALE, useValue: language },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    ModalHandler,
    Uptime,
    FormatIDDisplay,
    provideHttpClient(withInterceptorsFromDi()),
    provideMomentDateAdapter(LOCALE_DATE_FORMATS),
  ],
})
export class AppModule {}
